import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppIcon from '../AppIcon';
import Button from '../ui/Button';
import { usePermission } from '../../hooks/usePermission';
import {
  PERMISSION_META,
  requestCamera,
  requestGeolocation,
  type PermissionName,
} from '../../utils/permissions';

interface DevicePermissionsPanelProps {
  /**
   * Optional override for the notifications "Enable" action. Phase 3 (browser
   * web-push) passes a handler that registers the service worker + FCM token and
   * resolves once the subscription is live. When omitted we fall back to a bare
   * `Notification.requestPermission()` so the button still surfaces the prompt.
   */
  onEnableNotifications?: () => Promise<void>;
}

/**
 * A durable, state-aware list of the three device permissions the app uses
 * (location, camera, notifications). Each row reflects the live browser state and
 * offers only the action that can actually work:
 *  - prompt      → an "Enable" button that fires the real browser prompt.
 *  - granted     → a quiet "Allowed" pill (no action).
 *  - denied      → a "Blocked" label + an inline unblock hint (the web has no
 *                  programmatic deep-link to site settings, so we point at the
 *                  address-bar control instead of a button that would no-op).
 *  - unsupported → a quiet "not supported" note.
 */
const PermissionRow: React.FC<{
  name: PermissionName;
  onEnableNotifications?: () => Promise<void>;
}> = ({ name, onEnableNotifications }) => {
  const { t } = useTranslation();
  const { state, refresh } = usePermission(name);
  const [busy, setBusy] = useState(false);
  const meta = PERMISSION_META[name];

  const handleEnable = async () => {
    setBusy(true);
    try {
      if (name === 'geolocation') {
        await requestGeolocation();
      } else if (name === 'camera') {
        await requestCamera();
      } else if (onEnableNotifications) {
        await onEnableNotifications();
      } else if (typeof Notification !== 'undefined') {
        // Phase 3: wire full FCM web-push here (pass an onEnableNotifications prop
        // that registers the service worker + token). Until then, just surface the
        // native permission prompt so the state can move off `prompt`.
        await Notification.requestPermission();
      }
    } finally {
      setBusy(false);
      refresh();
    }
  };

  const control = (() => {
    switch (state) {
      case 'granted':
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2.5 py-1 text-xs font-medium text-success">
            <AppIcon name="Check" size={13} />
            {t('Allowed')}
          </span>
        );
      case 'prompt':
        return (
          <Button size="sm" variant="outline" loading={busy} onClick={handleEnable}>
            {t('Enable')}
          </Button>
        );
      case 'denied':
        return (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
            <AppIcon name="Lock" size={13} />
            {t('Blocked')}
          </span>
        );
      default:
        return (
          <span className="text-xs text-muted-foreground">
            {t('Not supported by your browser')}
          </span>
        );
    }
  })();

  return (
    <li className="flex items-start gap-3 py-4">
      <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
        <AppIcon name={meta.icon} size={18} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{t(meta.titleKey)}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{t(meta.descKey)}</p>
        {state === 'denied' && (
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
            {t('Blocked in your browser. Use the site-settings icon in the address bar to allow it.')}
          </p>
        )}
      </div>
      <div className="shrink-0 self-center">{control}</div>
    </li>
  );
};

const PERMISSION_ORDER: PermissionName[] = ['geolocation', 'camera', 'notifications'];

const DevicePermissionsPanel: React.FC<DevicePermissionsPanelProps> = ({
  onEnableNotifications,
}) => {
  return (
    <ul className="divide-y divide-border rounded-xl border border-border bg-card px-4">
      {PERMISSION_ORDER.map((name) => (
        <PermissionRow
          key={name}
          name={name}
          onEnableNotifications={onEnableNotifications}
        />
      ))}
    </ul>
  );
};

export default DevicePermissionsPanel;
